/**
 * useRealtimeInvalidation — connects to the backend WebSocket and
 * automatically invalidates TanStack Query cache when other users
 * modify data in the same tenant.
 *
 * Usage: call once in a top-level layout component (e.g. the
 * authenticated dashboard layout). The hook manages the WS
 * connection lifecycle — connects on mount, reconnects on drop,
 * disconnects on unmount.
 *
 * When a message arrives (e.g. `{ entity: "crm.accounts", id }`),
 * the hook marks the matching queries stale (`lib/realtime-notice.ts`).
 * TanStack Query handles the refetch automatically — active queries
 * refetch immediately, inactive ones the next time they're used.
 */

import { activityGate, ONLINE_ACTIVITY_MESSAGE } from '@cloud/shared/hr/online-activity'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { BACKEND_URL } from '@/lib/orpcClient'
import { noticeTouchesQuery, type RealtimeNotice, throttleByKey } from '@/lib/realtime-notice'

// Alleen de eerste verbinding na het laden van de pagina zegt `fresh=1`: dan opent de server een
// oranje registratie (urenregistratie). Een herverbinding of een nieuwe mount doet dat niet; een
// klik of toets daarna wel, als er dan niets open staat (`ONLINE_ACTIVITY_MESSAGE`).
let firstConnectOfPage = true

const RECONNECT_DELAY = 3000
const AREA_THROTTLE_MS = 3000
const MAX_RECONNECT_DELAY = 30000

export function useRealtimeInvalidation() {
  const queryClient = useQueryClient()
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectDelayRef = useRef(RECONNECT_DELAY)
  const isUnmountedRef = useRef(false)
  const droppedRef = useRef(false)

  useEffect(() => {
    isUnmountedRef.current = false
    const invalidateArea = throttleByKey((area) => {
      if (isUnmountedRef.current) return
      void queryClient.invalidateQueries({ queryKey: [area.split('.')] })
    }, AREA_THROTTLE_MS)

    function connect() {
      if (isUnmountedRef.current) return

      // Build the WS URL from the backend URL
      const wsUrl =
        BACKEND_URL.replace(/^http/, 'ws') + (firstConnectOfPage ? '/ws?fresh=1' : '/ws')
      firstConnectOfPage = false

      try {
        const ws = new WebSocket(wsUrl)
        wsRef.current = ws

        ws.onopen = () => {
          // Reset reconnect delay on successful connection
          reconnectDelayRef.current = RECONNECT_DELAY
          // Tijdens een onderbreking zijn meldingen gemist: alles geldt als verouderd.
          if (droppedRef.current) void queryClient.invalidateQueries()
          droppedRef.current = false
        }

        ws.onmessage = (event) => {
          if (isUnmountedRef.current) return
          try {
            const msg: RealtimeNotice = JSON.parse(event.data)
            // Wat op het scherm staat haalt meteen opnieuw op, de rest bij het volgende gebruik.
            if (msg.entity) {
              void queryClient.invalidateQueries({
                predicate: (query) => noticeTouchesQuery(msg, query.queryKey),
              })
            }
            for (const area of msg.also ?? []) invalidateArea(area)
          } catch {
            // Malformed message — ignore
          }
        }

        ws.onclose = () => {
          wsRef.current = null
          if (isUnmountedRef.current) return
          droppedRef.current = true
          // Reconnect with exponential backoff
          reconnectTimerRef.current = setTimeout(() => {
            reconnectDelayRef.current = Math.min(
              reconnectDelayRef.current * 1.5,
              MAX_RECONNECT_DELAY,
            )
            connect()
          }, reconnectDelayRef.current)
        }

        ws.onerror = () => {
          // onclose will fire after onerror — reconnect happens there
        }
      } catch {
        // WebSocket constructor can throw if URL is invalid
        if (!isUnmountedRef.current) {
          reconnectTimerRef.current = setTimeout(connect, reconnectDelayRef.current)
        }
      }
    }

    connect()

    const activity = activityGate()
    const onActivity = () => {
      const ws = wsRef.current
      if (ws?.readyState === WebSocket.OPEN && activity()) ws.send(ONLINE_ACTIVITY_MESSAGE)
    }
    const listen = { capture: true, passive: true } as const
    window.addEventListener('pointerdown', onActivity, listen)
    window.addEventListener('keydown', onActivity, listen)

    return () => {
      isUnmountedRef.current = true
      window.removeEventListener('pointerdown', onActivity, listen)
      window.removeEventListener('keydown', onActivity, listen)

      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }

      const ws = wsRef.current
      wsRef.current = null

      if (!ws) return

      // Detach listeners so onclose doesn't re-trigger a reconnect
      // after cleanup, and onmessage doesn't touch the query cache
      // on a torn-down provider tree.
      ws.onopen = null
      ws.onmessage = null
      ws.onerror = null
      ws.onclose = null

      if (ws.readyState === WebSocket.OPEN) {
        ws.close()
      } else if (ws.readyState === WebSocket.CONNECTING) {
        // Closing a CONNECTING socket directly emits a browser warning
        // ("closed before established"). Instead, wait until the socket
        // opens, then close it cleanly. Common in React strict mode's
        // double-mount: first mount creates the socket, cleanup fires
        // before it connects.
        ws.onopen = () => ws.close()
      }
      // readyState CLOSING (2) or CLOSED (3): nothing to do
    }
  }, [queryClient])
}
